import React, { useState, useEffect } from 'react';
import { Download, Search, Eye, FileText, X, User, Phone, Mail, MapPin, Calendar, BookOpen, Award, HeartPulse, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaCursosInscritos({ readOnly = false }) {
    const [inscritos, setInscritos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedInscrito, setSelectedInscrito] = useState(null);
    const currentYear = new Date().getFullYear();
    const [gestion, setGestion] = useState(currentYear);

    const calcularEdad = (fecha_nacimiento) => {
        if (!fecha_nacimiento) return '-';
        const fn = new Date(fecha_nacimiento);
        if (isNaN(fn.getTime())) return '-';
        const hoy = new Date();
        let edad = hoy.getFullYear() - fn.getFullYear();
        if (hoy.getMonth() < fn.getMonth() || (hoy.getMonth() === fn.getMonth() && hoy.getDate() < fn.getDate())) {
            edad--;
        }
        return Math.max(0, edad);
    };

    const formatGenero = (g) => {
        if (!g) return '-';
        const norm = g.toString().trim().toUpperCase();
        if (norm === 'M' || norm.startsWith('MASC')) return 'Masculino';
        if (norm === 'F' || norm.startsWith('FEME')) return 'Femenino';
        return g;
    };

    const getFileUrl = (path) => {
        if (!path) return '#';

        // If it's a base64 data URI, return it as is
        if (path.toString().startsWith('data:')) return path;

        // Get base URL without /api/v1
        const baseUrl = API_URL.replace('/api/v1', '');

        // Clean path separators
        let cleanPath = path.toString().replace(/\\/g, '/');

        // If it's an absolute path (contains 'uploads'), extract the relative part starting from /uploads
        const uploadsIndex = cleanPath.indexOf('/uploads/');
        if (uploadsIndex !== -1) {
            cleanPath = cleanPath.substring(uploadsIndex);
        } else {
            const uploadsIndexNoSlash = cleanPath.indexOf('uploads/');
            if (uploadsIndexNoSlash !== -1) {
                cleanPath = '/' + cleanPath.substring(uploadsIndexNoSlash);
            }
        }

        // Ensure path starts with / but doesn't double it
        const finalPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

        // Combine baseUrl and finalPath ensuring no double // between them
        const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        return `${base}${finalPath}`;
    };

    useEffect(() => {
        loadInscritos();
    }, [gestion]);

    const loadInscritos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/kardex/inscritos?gestion=${gestion}`);
            const data = await response.json();
            setInscritos(data.inscritos || []);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleObservacionChange = async (inscrito, observacion) => {
        try {
            await fetch(`${API_URL}/kardex/inscritos/${inscrito.id_inscripcion}/observaciones`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    observaciones: observacion,
                    tipo_usuario: inscrito.tipo_usuario
                })
            });
            // Update local state to avoid re-fetching
            setInscritos(prev => prev.map(i =>
                i.id_inscripcion === inscrito.id_inscripcion ? { ...i, observaciones: observacion } : i
            ));
        } catch (error) {
            console.error('Error updating observaciones:', error);
        }
    };

    const handleEstadoChange = async (inscrito, estado) => {
        try {
            const response = await fetch(
                `${API_URL}/kardex/inscritos/${inscrito.id_inscripcion}/estado-certificado`, // ← cambiado
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        estado_certificado: estado,
                        emitir: estado === 'aprobado',
                        tipo_usuario: inscrito.tipo_usuario
                    })
                }
            );
            if (response.ok) loadInscritos();
        } catch (error) {
            console.error('Error updating estado:', error);
        }
    };

    const exportarExcel = () => {
        try {
            if (!filteredInscritos || filteredInscritos.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            const data = filteredInscritos.map((i, idx) => {
                const edadCalc = calcularEdad(i.fecha_nacimiento);
                return {
                    'No': idx + 1,
                    'Nombres': i.nombres || '',
                    'Apellidos': i.apellidos || '',
                    'Cédula de Identidad': i.cedula || '',
                    'Extensión': i.extension || '',
                    'Fecha de Nacimiento': i.fecha_nacimiento ? new Date(i.fecha_nacimiento).toLocaleDateString() : '',
                    'Lugar de Nacimiento': i.lugar_nacimiento || '',
                    'Edad': edadCalc !== '-' ? edadCalc : '',
                    'Género': formatGenero(i.genero),
                    'Dirección': i.direccion || '',
                    'Teléfono': i.telefono || '',
                    'Correo Electrónico': i.email || '',
                    'Contacto de Emergencia': i.contacto_nombre ? `${i.contacto_nombre} (${i.contacto_parentesco || '-'})` : '',
                    'Teléfono de Emergencia': i.contacto_telefono || '',
                    'Nombre Curso': i.curso_nombre || '',
                    'Fecha de Inscripción': i.fecha_inscripcion ? new Date(i.fecha_inscripcion).toLocaleDateString() : '',
                    'Número de Cuotas': i.numero_cuotas || '',
                    'Estado Certificado': i.estado_certificado || (i.certificado_aprobado ? 'Aprobado' : 'Pendiente'),
                    'Observaciones': i.observaciones || ''
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Inscritos');
            XLSX.writeFile(wb, `Inscritos_Cursos_Extension_${gestion}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Inscritos:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const verDocumentos = (inscrito) => {
        setSelectedInscrito(inscrito);
        setShowDocsModal(true);
    };

    const verDetalles = (inscrito) => {
        setSelectedInscrito(inscrito);
        setShowDetailsModal(true);
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'aprobado': return 'bg-green-100 text-green-800';
            case 'rechazado': return 'bg-red-100 text-red-800';
            default: return 'bg-yellow-100 text-yellow-800';
        }
    };

    const filteredInscritos = inscritos.filter(i => {
        if (searchTerm === '') return true;
        const search = searchTerm.toLowerCase();
        const fullName = `${i.nombres || ''} ${i.apellidos || ''}`.toLowerCase();
        const cedula = (i.cedula || '').toLowerCase();
        const email = (i.email || '').toLowerCase();
        const curso = (i.curso_nombre || '').toLowerCase();

        return fullName.includes(search) ||
            cedula.includes(search) ||
            email.includes(search) ||
            curso.includes(search);
    });

    return (
        <div className="p-4 md:p-6 overflow-hidden">
            <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-800">
                    {!readOnly ? 'Jefatura de Carrera > Cursos de Extensión > Inscritos' : 'Dirección Académica > Cursos de Extensión > Inscritos'}
                </h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={gestion}
                            onChange={(e) => setGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>



                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, apellido o CI..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button
                            onClick={exportarExcel}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Descargar Excel"
                        >
                            <Download size={20} />
                        </button>

                    </div>
                </div>
            </div>

            {/* Barra de controles */}
            {/* <div className="mb-4 flex items-center gap-3">
                <select
                    value={gestion}
                    onChange={(e) => setGestion(parseInt(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>

                <div className="flex-1 max-w-md relative">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, apellido o CI..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-full"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>

                <button
                    onClick={exportarExcel}
                    className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                    title="Descargar Excel"
                >
                    <Download size={20} />
                </button>
            </div> */}

            {/* Tabla de Inscritos - Igual a Kardex */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto max-w-full" style={{ maxWidth: 'calc(100vw - 280px)' }}>
                    <table className="w-full text-xs whitespace-nowrap">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-3 text-center font-semibold border">No</th>
                                <th className="px-2 py-3 text-center font-semibold border">Nombres</th>
                                <th className="px-2 py-3 text-center font-semibold border">Apellidos</th>
                                <th className="px-2 py-3 text-center font-semibold border">Cédula de Identidad</th>
                                <th className="px-2 py-3 text-center font-semibold border">Extensión</th>
                                <th className="px-2 py-3 text-center font-semibold border">Correo Electrónico</th>
                                <th className="px-2 py-3 text-center font-semibold border">Nombre Curso</th>
                                <th className="px-2 py-3 text-center font-semibold border">Estado Certificado</th>
                                <th className="px-2 py-3 text-center font-semibold border">Acciones</th>
                                <th className="px-2 py-3 text-center font-semibold border">Observaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500 border">Cargando...</td></tr>
                            ) : filteredInscritos.length === 0 ? (
                                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500 border">No hay inscritos registrados</td></tr>
                            ) : filteredInscritos.map((inscrito, idx) => (
                                <tr key={inscrito.id_inscripcion} className="hover:bg-gray-50">
                                    <td className="px-2 py-3 text-center border">{idx + 1}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.nombres || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.apellidos || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.cedula || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.extension || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.email || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{inscrito.curso_nombre || '-'}</td>
                                    <td className="px-2 py-3 text-center border">
                                        {!readOnly ? (
                                            <select
                                                value={inscrito.estado_certificado || (inscrito.certificado_aprobado ? 'aprobado' : 'pendiente')}
                                                onChange={(e) => handleEstadoChange(inscrito, e.target.value)}
                                                className={`px-2 py-1 rounded text-xs font-medium border-0 ${getEstadoColor(inscrito.estado_certificado || (inscrito.certificado_aprobado ? 'aprobado' : 'pendiente'))}`}
                                            >
                                                <option value="pendiente">Pendiente</option>
                                                <option value="aprobado">Aprobar Emisión</option>
                                                <option value="rechazado">Rechazar Emisión</option>
                                            </select>
                                        ) : (
                                            <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getEstadoColor(inscrito.estado_certificado || (inscrito.certificado_aprobado ? 'aprobado' : 'pendiente'))}`}>
                                                {inscrito.estado_certificado || (inscrito.certificado_aprobado ? 'aprobado' : 'pendiente')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 py-3 text-center border">
                                        <div className="flex justify-center gap-1">
                                            <button
                                                onClick={() => verDetalles(inscrito)}
                                                className="p-2 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200"
                                                title="Ver detalles completos"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => verDocumentos(inscrito)}
                                                className="p-2 rounded-full transition shadow-sm bg-green-100 text-green-600 hover:bg-green-200"
                                                title="Ver documentos"
                                            >
                                                <FileText size={16} />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 border">
                                        <textarea
                                            defaultValue={inscrito.observaciones || ''}
                                            onBlur={(e) => handleObservacionChange(inscrito, e.target.value)}
                                            placeholder="Añadir observación..."
                                            className="w-full min-w-[200px] px-2 py-1 text-xs border border-gray-200 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none resize-none"
                                            rows={1}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Ver Detalles */}
            {showDetailsModal && selectedInscrito && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-xl font-bold text-blue-900">Detalles del Inscrito</h3>
                            <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            {/* SECCIÓN 1: DATOS PERSONALES */}
                            <div className="mb-6">
                                <h4 className="text-blue-900 font-bold mb-4 flex items-center gap-2 pb-2">
                                    {/* <User size={18} /> */}
                                    Datos Personales
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-gray-500 font-medium">Nombres</label>
                                        <p className="text-gray-900">{selectedInscrito.nombres || '-'}</p>
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-gray-500 font-medium">Apellidos</label>
                                        <p className="text-gray-900">{selectedInscrito.apellidos || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Documento de Identidad</label>
                                        <p className="text-gray-900">{selectedInscrito.cedula || '-'} {selectedInscrito.extension || ''}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Género</label>
                                        <p className="text-gray-900">{formatGenero(selectedInscrito.genero)}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Fecha de Nacimiento</label>
                                        <div className="flex items-center gap-1">
                                            <Calendar size={14} className="text-gray-400" />
                                            <p className="text-gray-900">
                                                {selectedInscrito.fecha_nacimiento ? new Date(selectedInscrito.fecha_nacimiento).toLocaleDateString() : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Edad</label>
                                        <p className="text-gray-900">{calcularEdad(selectedInscrito.fecha_nacimiento)} años</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-gray-500 font-medium">Lugar de Nacimiento</label>
                                        <p className="text-gray-900">{selectedInscrito.lugar_nacimiento || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN 2: CONTACTO Y UBICACIÓN */}
                            <div className="mb-6">
                                <h4 className="text-blue-900 font-bold mb-4 flex items-center gap-2 pb-2">
                                    {/* <MapPin size={18} /> */}
                                    Contacto y Ubicación
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <label className="text-gray-500 font-medium">Teléfono / Celular</label>
                                        <div className="flex items-center gap-1">
                                            <Phone size={14} className="text-gray-400" />
                                            <p className="text-gray-900">{selectedInscrito.telefono || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="text-gray-500 font-medium">Correo Electrónico</label>
                                        <div className="flex items-center gap-1">
                                            <Mail size={14} className="text-gray-400" />
                                            <p className="text-gray-900 truncate">{selectedInscrito.email || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-gray-500 font-medium">Dirección de Domicilio</label>
                                        <p className="text-gray-900">{selectedInscrito.direccion || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN 3: CONTACTO DE EMERGENCIA */}
                            <div className="mb-6">
                                <h4 className="text-blue-900 font-bold mb-4 flex items-center gap-2 pb-2">
                                    {/* <Info size={18} /> */}
                                    Contacto de Emergencia
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 p-3 rounded-lg">
                                    <div>
                                        <label className="text-gray-500 font-medium">Nombre Completo</label>
                                        <p className="text-gray-900">{selectedInscrito.contacto_nombre ? `${selectedInscrito.contacto_nombre} (${selectedInscrito.contacto_parentesco || '-'})` : '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Teléfono de Emergencia</label>
                                        <div className="flex items-center gap-2">
                                            <Phone size={14} className="text-green-600" />
                                            <p className="text-gray-900 font-bold">{selectedInscrito.contacto_telefono || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN 4: INFORMACIÓN DEL CURSO */}
                            <div className="mb-6">
                                <h4 className="text-blue-900 font-bold mb-4 flex items-center gap-2 pb-2">
                                    {/* <BookOpen size={18} /> */}
                                    Detalles de la Inscripción
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="col-span-2">
                                        <label className="text-gray-500 font-medium">Curso de Extensión</label>
                                        <p className="text-gray-900 font-bold text-base">{selectedInscrito.curso_nombre || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Fecha de Inscripción</label>
                                        <p className="text-gray-900">{selectedInscrito.fecha_inscripcion ? new Date(selectedInscrito.fecha_inscripcion).toLocaleDateString() : '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Número de Cuotas</label>
                                        <p className="text-gray-900">{selectedInscrito.numero_cuotas || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-gray-500 font-medium">Estado Certificado</label>
                                        <div className="mt-1">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getEstadoColor(selectedInscrito.estado_certificado || (selectedInscrito.certificado_aprobado ? 'aprobado' : 'pendiente'))}`}>
                                                <Award size={12} className="inline mr-1" />
                                                {selectedInscrito.estado_certificado || (selectedInscrito.certificado_aprobado ? 'Aprobado' : 'Pendiente')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="p-4 border-t flex justify-end">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver Documentos */}
            {showDocsModal && selectedInscrito && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-lg">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Documentos de {selectedInscrito.nombres} {selectedInscrito.apellidos}</h2>
                            <button onClick={() => setShowDocsModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-sm text-gray-500">Cédula de Identidad</label>
                                {selectedInscrito.doc_cedula ? (
                                    <a
                                        href={getFileUrl(selectedInscrito.doc_cedula)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-blue-600 hover:underline"
                                    >
                                        Ver documento
                                    </a>
                                ) : (
                                    <p className="text-gray-400">No adjuntado</p>
                                )}
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Comprobante de Pago</label>
                                {selectedInscrito.doc_comprobante_curso ? (
                                    <a
                                        href={getFileUrl(selectedInscrito.doc_comprobante_curso)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-blue-600 hover:underline"
                                    >
                                        Ver documento
                                    </a>
                                ) : (
                                    <p className="text-gray-400">No adjuntado</p>
                                )}
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Cédula del Tutor</label>
                                {selectedInscrito.doc_cedula_tutor ? (
                                    <a
                                        href={getFileUrl(selectedInscrito.doc_cedula_tutor)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-blue-600 hover:underline"
                                    >
                                        Ver documento
                                    </a>
                                ) : (
                                    <p className="text-gray-400">No adjuntado</p>
                                )}
                            </div>
                        </div>
                        <div className="p-4 border-t flex justify-end">
                            <button
                                onClick={() => setShowDocsModal(false)}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
